import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useFees } from '../../context/FeeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FinancialOverview = ({ user, stats, bookings, effectiveRole }) => {
  const { t } = useTranslation();
  const [financialSummary, setFinancialSummary] = useState(null);
  const { feeRates } = useFees();
  const providerFeeRate = feeRates.provider_fee_rate / 100; // Convert to decimal
  const clientFeeRate = feeRates.client_fee_rate / 100; // Convert to decimal

  useEffect(() => {
    if (effectiveRole === 'provider') {
      // Fetch financial summary from backend
      axios.get(`${API}/financial/summary`)
        .then(res => setFinancialSummary(res.data))
        .catch(err => console.error('Error fetching financial summary:', err));
    }
  }, [effectiveRole]);

  if (effectiveRole === 'provider') {
    // Calculate earnings from completed bookings using dynamic fee rate
    const completedBookings = bookings.filter(b => b.status === 'completed');
    const totalEarningsFromBookings = completedBookings.reduce((sum, b) => {
      const amount = b.final_amount || b.price || 0;
      return sum + (amount * (1 - providerFeeRate)); // After dynamic platform fee
    }, 0);

    // Use financialSummary if available, otherwise use calculated values
    const totalEarnings = financialSummary?.total_earnings || totalEarningsFromBookings;
    const availableBalance = financialSummary?.available_balance ?? user?.available_balance ?? 0;
    const pendingPayouts = totalEarnings - availableBalance - (financialSummary?.total_withdrawn || 0);

    return (
      <div className="bg-white p-6 rounded-2xl border border-gray-100 mb-8" data-testid="dashboard-earnings">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('ux.earningsFinances')}
          </h2>
          <Link 
            to="/earnings" 
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {t('ux.viewDetails')}
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-xl border border-green-200">
            <p className="text-green-700 text-sm font-medium mb-1">{t('ux.totalEarnings')}</p>
            <p className="text-2xl font-bold text-green-800">₱{totalEarnings.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
            <p className="text-xs text-green-600 mt-1">{t('ux.afterFee', { rate: feeRates.provider_fee_rate })}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border border-blue-200">
            <p className="text-blue-700 text-sm font-medium mb-1">{t('ux.availableBalance')}</p>
            <p className="text-2xl font-bold text-blue-800">₱{availableBalance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
            <p className="text-xs text-blue-600 mt-1">{t('ux.readyForWithdrawal')}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-5 rounded-xl border border-amber-200">
            <p className="text-amber-700 text-sm font-medium mb-1">{t('ux.pendingPayouts')}</p>
            <p className="text-2xl font-bold text-amber-800">₱{Math.max(0, pendingPayouts).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
            <p className="text-xs text-amber-600 mt-1">{t('ux.inEscrowProcessing')}</p>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{t('ux.platformFeeRate')}</p>
              <p className="text-lg font-semibold text-gray-900">{t('ux.deductedFromEarnings', { rate: feeRates.provider_fee_rate })}</p>
            </div>
            <Link 
              to="/earnings" 
              className="bg-gray-900 text-white px-5 py-2.5 rounded-full font-medium hover:bg-gray-800 transition text-sm"
            >
              {t('ux.withdrawFunds')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Client view - For Release: paid but not yet completed (in escrow)
  const forReleaseBookings = bookings.filter(b => b.payment_status === 'paid' && b.status !== 'completed');
  const forReleaseServiceAmount = forReleaseBookings.reduce((sum, b) => sum + (b.final_amount || b.price || 0), 0);
  const forReleaseWithFees = forReleaseServiceAmount + (forReleaseServiceAmount * clientFeeRate);

  // Total Spent: completed bookings that were ACTUALLY paid (not legacy completed-without-payment)
  const completedBookings = bookings.filter(b => b.status === 'completed' && b.payment_status === 'paid');
  const totalServiceAmount = completedBookings.reduce((sum, b) => sum + (b.final_amount || b.price || 0), 0);
  const totalPlatformFees = totalServiceAmount * clientFeeRate;  // dynamic client fee
  const totalSpent = totalServiceAmount + totalPlatformFees;
  const pendingPayments = bookings.filter(b => b.status === 'accepted' && !b.payment_status).length;
  
  return (
    <div className="bg-white p-6 rounded-2xl border border-gray-100 mb-8" data-testid="dashboard-spending">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          {t('ux.paymentsSpending')}
        </h2>
        <Link 
          to="/payments" 
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          {t('ux.viewAllPayments')}
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-5 rounded-xl border border-purple-200" data-testid="for-release-card">
          <p className="text-purple-700 text-sm font-medium mb-1">{t('ux.forRelease')}</p>
          <p className="text-2xl font-bold text-purple-800">₱{forReleaseWithFees.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
          <p className="text-xs text-purple-600 mt-1">{t('ux.awaitingCompletion')}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border border-blue-200" data-testid="total-spent-card">
          <p className="text-blue-700 text-sm font-medium mb-1">{t('ux.totalSpent')}</p>
          <p className="text-2xl font-bold text-blue-800">₱{totalSpent.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
          <p className="text-xs text-blue-600 mt-1">{t('ux.releasedAfter')}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-xl border border-green-200">
          <p className="text-green-700 text-sm font-medium mb-1">{t('ux.completedBookings')}</p>
          <p className="text-2xl font-bold text-green-800">{completedBookings.length}</p>
          <p className="text-xs text-green-600 mt-1">{t('ux.successfullyCompleted')}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-5 rounded-xl border border-amber-200">
          <p className="text-amber-700 text-sm font-medium mb-1">{t('ux.pendingPayments')}</p>
          <p className="text-2xl font-bold text-amber-800">{pendingPayments}</p>
          <p className="text-xs text-amber-600 mt-1">{t('ux.awaitingPayment')}</p>
        </div>
      </div>
      
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{t('ux.platformServiceFee')}</p>
            <p className="text-lg font-semibold text-gray-900">{t('ux.addedToServiceAmount', { rate: feeRates.client_fee_rate })}</p>
            <p className="text-xs text-gray-500 mt-1">{t('ux.egServiceYouPay', { amount: (1000 * (1 + clientFeeRate)).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0}) })}</p>
          </div>
          <Link 
            to="/payments" 
            className="bg-gray-900 text-white px-5 py-2.5 rounded-full font-medium hover:bg-gray-800 transition text-sm"
          >
            {t('ux.paymentHistory')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FinancialOverview;
